import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormHelperText, makeStyles } from '@material-ui/core';
import CustomButton from '../customButton';
import Menu from './menu';
import NotificationCard from '../notificationCard';
import VirtualizedList from '../virtualizedList';
import VirtualSelectedUsers from '../virtualSelectedUsers';
import SelectedFiltersActions from './selectedFiltersActions';
import SelectedFilters from './selectedFilters';
import { isArray, isArrayEmpty, trimString } from '../../../utility/helpers';
import { useElementMaxHeight } from '../../../utility/hooks';
import {
  resetFilters,
  isDirty,
  prepareInitialSelectedFilters,
  prepareAllSelectedFilters,
} from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterBlock: {
    gap: spacing(2),
  },
  filters: {
    display: 'grid',
    gap: spacing(2),
  },
  filterButton: {
    flexShrink: 0,
    borderRadius: 54,
  },
  filter: {
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    lineHeight: '24px',
    marginRight: spacing(2),
  },
  filterLabel: {
    maxWidth: 150,
  },
  filterTypeLabel: {
    marginRight: spacing(1.5),
    maxWidth: 150,
    width: 'fit-content',
    minWidth: 'fit-content',
  },
  clearCategoryIcon: {
    width: 12,
    height: 12,
  },
  actionButton: {
    width: 24,
    height: 24,
  },
  selectedItemsViewport: {
    maxHeight: ({ shouldCalculateSelectionHeight, selectionMaxHeight }) =>
      shouldCalculateSelectionHeight ? selectionMaxHeight : 528,
  },
  selectedItemsWrapper: {
    marginTop: spacing(6),
  },
  selectedItemsWrapperError: {
    marginTop: spacing(1),
  },
  selectedItemsScrollY: {
    right: -15,
  },
  selectedViewport: {
    maxHeight: 100,
  },
  selectedItemWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  item: {
    boxSizing: 'border-box',
    position: 'absolute',
    top: 0,
    left: 0,
    contain: 'layout style paint',
    willChange: 'transform',
    overflow: 'hidden',
    width: '100%',
  },
  scrollY: {
    right: -15,
  },
  notificationCard: {
    marginTop: spacing(6),
  },
  notificationCardError: {
    marginTop: spacing(1),
  },
}));

const CollapsableFilters = ({
  className = null,
  customErrorClass = '',
  selectedItemsScrollYClass = undefined,
  translations,
  parentNode,
  parentOffset = 0,
  filters,
  selectedFilters,
  values = [],
  showSelection = false,
  shouldCalculateSelectionHeight = false,
  hasError = false,
  isDisabled = false,
  onApplyFilters,
  errorMessage = '',
}) => {
  const initialSelectedFilters = useMemo(
    () => prepareInitialSelectedFilters(filters, selectedFilters),
    [filters, selectedFilters]
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const [tempFilters, setTempFilters] = useState(
    prepareInitialSelectedFilters(filters, initialSelectedFilters)
  );
  const [search, setSearch] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const allSelectedFilters = useMemo(
    () => prepareAllSelectedFilters(filters, initialSelectedFilters),
    [filters, initialSelectedFilters]
  );
  const hasSelectedFilters = useMemo(
    () => !isArrayEmpty(allSelectedFilters),
    [allSelectedFilters]
  );

  const [setElementRef, selectionMaxHeight, calculateHeight] =
    useElementMaxHeight(
      parentNode,
      !hasError &&
        showSelection &&
        shouldCalculateSelectionHeight &&
        !isArrayEmpty(values),
      parentOffset
    );

  const classes = useStyles({
    selectionMaxHeight,
    shouldCalculateSelectionHeight,
  });

  const handleApplyFilters = () => {
    const { id, parent } = selectedSubMenu;
    const currentSubMenuId = parent?.id || id;

    // Check if submenu filters are changed
    if (
      isDirty(
        initialSelectedFilters[currentSubMenuId],
        tempFilters[currentSubMenuId]
      )
    ) {
      onApplyFilters(tempFilters);
    }
  };

  const onToggleExpandSelectedFilters = () =>
    setIsExpanded(prevExpanded => !prevExpanded);

  const onSearch = value => setSearch(trimString(value));

  const handleClose = e => {
    e.stopPropagation();
    setAnchorEl(null);
    setSelectedSubMenu(null);
    setTempFilters(initialSelectedFilters);
    setSearch('');
  };

  const handleOpenPrimaryMenu = e => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const onToggleSubMenu = newSelected => () => {
    setSelectedSubMenu(
      newSelected.id === selectedSubMenu?.id ? null : newSelected
    );
    setTempFilters(initialSelectedFilters);
    setSearch('');
  };

  const onSelectFilter = selectedFilter => () => {
    const { id, parent, isSingleSelect } = selectedSubMenu;
    const currentSubMenuId = parent?.id || id;
    const selectedFilterIndex = tempFilters[currentSubMenuId].findIndex(
      filter => filter === selectedFilter
    );

    if (isSingleSelect) {
      return setTempFilters(prevFilters => ({
        ...prevFilters,
        [id]: [...(selectedFilter !== null ? [selectedFilter] : [])],
      }));
    }

    if (selectedFilterIndex === -1) {
      return setTempFilters(prevFilters => ({
        ...prevFilters,
        [currentSubMenuId]: [...prevFilters[currentSubMenuId], selectedFilter],
      }));
    }

    setTempFilters(prevFilters => ({
      ...prevFilters,
      [currentSubMenuId]: [
        ...prevFilters[currentSubMenuId].slice(0, selectedFilterIndex),
        ...prevFilters[currentSubMenuId].slice(
          selectedFilterIndex + 1,
          prevFilters[currentSubMenuId].length
        ),
      ],
    }));
  };

  const onRemoveFilter = (filterId, filterItemId) => {
    const updatedFilters = {
      ...tempFilters,
      [filterId]: tempFilters[filterId].filter(item => item !== filterItemId),
    };

    setTempFilters(updatedFilters);
    onApplyFilters(updatedFilters);
  };

  const onClearAll = () => {
    const updatedFilters = resetFilters(tempFilters);

    setTempFilters(updatedFilters);
    return onApplyFilters(updatedFilters);
  };

  const getPreselectedVirtualItemSize = useCallback(
    index => (index === values.length - 1 ? 24 : 40),
    [values]
  );

  useEffect(() => {
    calculateHeight();

    if (selectedSubMenu) {
      handleApplyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempFilters, calculateHeight]);

  useEffect(() => {
    if (hasSelectedFilters) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [hasSelectedFilters]);

  const renderFilteredItem = useCallback(filteredItem => {
    return <VirtualSelectedUsers item={filteredItem} />;
  }, []);

  return (
    <div>
      <div className={classNames(classes.main, className)}>
        <CustomButton
          className={classes.filterButton}
          type="filters"
          onClick={handleOpenPrimaryMenu}
          disabled={isDisabled}
        >
          {translations.filters}
        </CustomButton>
        <SelectedFiltersActions
          translations={translations}
          isExpanded={isExpanded}
          hasSelectedFilters={hasSelectedFilters}
          isDisabled={isDisabled}
          onSelect={onToggleExpandSelectedFilters}
          onClear={onClearAll}
        />
      </div>
      {hasError && errorMessage && (
        <FormHelperText className={customErrorClass} error={hasError}>
          {errorMessage}
        </FormHelperText>
      )}
      <SelectedFilters
        translations={translations}
        selectedFilters={allSelectedFilters}
        isExpanded={isExpanded}
        calculateHeight={calculateHeight}
        onRemoveFilter={onRemoveFilter}
      />
      {showSelection && !isArrayEmpty(values) && (
        <div
          ref={setElementRef}
          className={classNames(classes.selectedItemsWrapper, {
            [classes.selectedItemsWrapperError]:
              hasError && !hasSelectedFilters,
          })}
        >
          <VirtualizedList
            viewportClass={classes.selectedItemsViewport}
            verticalScrollClass={classNames(
              classes.selectedItemsScrollY,
              selectedItemsScrollYClass
            )}
            itemClass={classes.selectedItemWrapper}
            items={values}
            count={values?.length ?? 0}
            gap={16}
            estimateSize={getPreselectedVirtualItemSize}
            renderItem={renderFilteredItem}
          />
        </div>
      )}
      <NotificationCard
        className={classNames(classes.notificationCard, {
          [classes.notificationCardError]: hasError && !hasSelectedFilters,
        })}
        title={translations.noResults}
        content={translations.noResultsContent}
        shouldFade={showSelection && isArrayEmpty(values)}
      />
      {isArray(filters) && !isArrayEmpty(filters) && (
        <Menu
          translations={translations}
          anchorEl={anchorEl}
          menuItems={filters}
          selectedSubMenu={selectedSubMenu}
          selectedFilters={tempFilters}
          searchTerm={search}
          onSelectFilter={onSelectFilter}
          onFilterSearch={onSearch}
          onToggleSubMenu={onToggleSubMenu}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

CollapsableFilters.propTypes = {
  className: PropTypes.string,
  selectedItemsScrollYClass: PropTypes.string,
  customErrorClass: PropTypes.string,
  translations: PropTypes.object.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedFilters: PropTypes.shape({}).isRequired,
  values: PropTypes.arrayOf(PropTypes.shape({})),
  errorMessage: PropTypes.string,
  parentOffset: PropTypes.number,
  isDisabled: PropTypes.bool,
  showSelection: PropTypes.bool,
  shouldCalculateSelectionHeight: PropTypes.bool,
  hasError: PropTypes.bool,
  onApplyFilters: PropTypes.func.isRequired,
};

export default CollapsableFilters;
